import * as types from '../mutation-types';
import BillingAPI from '../../api/billing';
import { throwErrorMessage } from '../utils/api';

const state = {
  subscription: null,
  addons: [],
  limits: {},
  usage: {},
  plans: [],
  availableAddons: [],
  paymentMethods: [],
  supportInfo: {
    email: '',
    whatsapp: '',
    inbox_id: '',
  },
  // Pending payment info (for PIX/Boleto)
  pendingPayment: null,
  uiFlags: {
    isFetching: false,
    isFetchingPlans: false,
    isFetchingAddons: false,
    isFetchingPaymentMethods: false,
    isCheckoutInProcess: false,
    isUpdating: false,
  },
};

export const getters = {
  getSubscription: $state => $state.subscription,
  getAddons: $state => $state.addons,
  getLimits: $state => $state.limits,
  getUsage: $state => $state.usage,
  getPlans: $state => $state.plans,
  getAvailableAddons: $state => $state.availableAddons,
  getPaymentMethods: $state => $state.paymentMethods,
  getSupportInfo: $state => $state.supportInfo,
  getPendingPayment: $state => $state.pendingPayment,
  getUIFlags: $state => $state.uiFlags,

  // Computed getters
  isTrialing: $state => $state.subscription?.in_trial || false,
  isActive: $state =>
    ['active', 'trialing'].includes($state.subscription?.status),
  isPastDue: $state => $state.subscription?.status === 'past_due',
  isCancelled: $state => $state.subscription?.status === 'cancelled',

  currentPlan: $state => $state.subscription?.plan || null,
  trialDaysRemaining: $state => $state.subscription?.trial_days_remaining || 0,
  daysUntilRenewal: $state => $state.subscription?.days_until_renewal || 0,

  // Check if multiple payment methods are available
  hasMultiplePaymentMethods: $state => $state.paymentMethods.length > 1,

  // Usage percentages
  getUsagePercentage: $state => resource => {
    const limit = $state.limits[resource];
    const used = $state.usage[resource];
    if (!limit || limit === 0) return 0;
    return Math.min(100, Math.round((used / limit) * 100));
  },

  // Check if limit is reached
  isLimitReached: $state => resource => {
    const limit = $state.limits[resource];
    const used = $state.usage[resource];
    if (!limit || limit === 0) return false;
    return used >= limit;
  },
};

export const actions = {
  // Fetch all billing information
  fetchBillingInfo: async ({ commit }) => {
    commit(types.default.SET_BILLING_UI_FLAG, { isFetching: true });
    try {
      const response = await BillingAPI.getBillingInfo();
      const { subscription, addons, limits, usage } = response.data;

      commit(types.default.SET_BILLING_SUBSCRIPTION, subscription);
      commit(types.default.SET_BILLING_ADDONS, addons);
      commit(types.default.SET_BILLING_LIMITS, limits);
      commit(types.default.SET_BILLING_USAGE, usage);
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.default.SET_BILLING_UI_FLAG, { isFetching: false });
    }
  },

  // Fetch available plans
  fetchPlans: async ({ commit }) => {
    commit(types.default.SET_BILLING_UI_FLAG, { isFetchingPlans: true });
    try {
      const response = await BillingAPI.getPlans();
      commit(types.default.SET_BILLING_PLANS, response.data.plans);
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.default.SET_BILLING_UI_FLAG, { isFetchingPlans: false });
    }
  },

  // Fetch available addons
  fetchAvailableAddons: async ({ commit }) => {
    commit(types.default.SET_BILLING_UI_FLAG, { isFetchingAddons: true });
    try {
      const response = await BillingAPI.getAvailableAddons();
      commit(types.default.SET_BILLING_AVAILABLE_ADDONS, response.data.addons);
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.default.SET_BILLING_UI_FLAG, { isFetchingAddons: false });
    }
  },

  // Fetch available payment methods
  fetchPaymentMethods: async ({ commit }) => {
    commit(types.default.SET_BILLING_UI_FLAG, {
      isFetchingPaymentMethods: true,
    });
    try {
      const response = await BillingAPI.getPaymentMethods();
      commit(
        types.default.SET_BILLING_PAYMENT_METHODS,
        response.data.payment_methods
      );
    } catch (error) {
      // Don't throw - payment methods are optional
      // eslint-disable-next-line no-console
      console.warn('Failed to fetch payment methods:', error);
      commit(types.default.SET_BILLING_PAYMENT_METHODS, []);
    } finally {
      commit(types.default.SET_BILLING_UI_FLAG, {
        isFetchingPaymentMethods: false,
      });
    }
  },

  // Checkout (redirect to payment gateway or show PIX/Boleto)
  checkout: async (
    { commit },
    { planId, billingCycle, paymentMethod, successUrl, cancelUrl } = {}
  ) => {
    commit(types.default.SET_BILLING_UI_FLAG, { isCheckoutInProcess: true });
    commit(types.default.SET_BILLING_PENDING_PAYMENT, null);

    try {
      const response = await BillingAPI.checkout({
        plan_id: planId,
        billing_cycle: billingCycle,
        payment_method: paymentMethod,
        success_url: successUrl,
        cancel_url: cancelUrl,
      });

      const { data } = response;

      // Handle different payment types
      if (data.checkout_url) {
        // Redirect to external checkout (Stripe or Asaas credit card)
        window.location.href = data.checkout_url;
        return { type: 'redirect', url: data.checkout_url };
      }

      if (data.payment_type === 'pix') {
        // PIX payment - show QR code
        commit(types.default.SET_BILLING_PENDING_PAYMENT, {
          type: 'pix',
          paymentId: data.payment_id,
          subscriptionId: data.subscription_id,
          qrCode: data.pix_qrcode,
          copyPaste: data.pix_copy_paste,
          expirationDate: data.expiration_date,
          value: data.value,
          cycle: data.cycle,
          isRecurring: data.is_recurring || false,
        });
        return { type: 'pix', data };
      }

      if (data.payment_type === 'boleto') {
        // Boleto payment - show boleto info
        commit(types.default.SET_BILLING_PENDING_PAYMENT, {
          type: 'boleto',
          paymentId: data.payment_id,
          subscriptionId: data.subscription_id,
          boletoUrl: data.boleto_url,
          barcode: data.boleto_barcode,
          dueDate: data.due_date,
          value: data.value,
          cycle: data.cycle,
          isRecurring: data.is_recurring || false,
        });
        return { type: 'boleto', data };
      }

      return { type: 'unknown', data };
    } catch (error) {
      throwErrorMessage(error);
      return { type: 'error', error };
    } finally {
      commit(types.default.SET_BILLING_UI_FLAG, { isCheckoutInProcess: false });
    }
  },

  // Go to billing portal
  openPortal: async ({ commit }, returnUrl) => {
    commit(types.default.SET_BILLING_UI_FLAG, { isCheckoutInProcess: true });
    try {
      const response = await BillingAPI.getPortalUrl(returnUrl);

      if (response.data.portal_url) {
        window.location.href = response.data.portal_url;
      }
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.default.SET_BILLING_UI_FLAG, { isCheckoutInProcess: false });
    }
  },

  // Change plan
  changePlan: async ({ commit, dispatch }, { planId, billingCycle }) => {
    commit(types.default.SET_BILLING_UI_FLAG, { isUpdating: true });
    try {
      await BillingAPI.changePlan(planId, billingCycle);
      await dispatch('fetchBillingInfo');
      return true;
    } catch (error) {
      throwErrorMessage(error);
      return false;
    } finally {
      commit(types.default.SET_BILLING_UI_FLAG, { isUpdating: false });
    }
  },

  // Cancel subscription
  cancelSubscription: async ({ commit, dispatch }, immediately = false) => {
    commit(types.default.SET_BILLING_UI_FLAG, { isUpdating: true });
    try {
      await BillingAPI.cancelSubscription(immediately);
      await dispatch('fetchBillingInfo');
      return true;
    } catch (error) {
      throwErrorMessage(error);
      return false;
    } finally {
      commit(types.default.SET_BILLING_UI_FLAG, { isUpdating: false });
    }
  },

  // Add addon (redirects to checkout or shows PIX/Boleto)
  addAddon: async (
    { commit },
    { addonId, quantity, billingCycle, paymentMethod }
  ) => {
    commit(types.default.SET_BILLING_UI_FLAG, { isUpdating: true });
    commit(types.default.SET_BILLING_PENDING_PAYMENT, null);

    try {
      const response = await BillingAPI.addAddon(
        addonId,
        quantity,
        billingCycle,
        paymentMethod
      );
      const { data } = response;

      // Handle different payment types
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
        return { type: 'redirect', url: data.checkout_url };
      }

      if (data.payment_type === 'pix') {
        commit(types.default.SET_BILLING_PENDING_PAYMENT, {
          type: 'pix',
          paymentId: data.payment_id,
          subscriptionId: data.subscription_id,
          qrCode: data.pix_qrcode,
          copyPaste: data.pix_copy_paste,
          expirationDate: data.expiration_date,
          value: data.value,
          cycle: data.cycle,
          isRecurring: data.is_recurring || false,
          addonId: addonId,
        });
        return { type: 'pix', data };
      }

      if (data.payment_type === 'boleto') {
        commit(types.default.SET_BILLING_PENDING_PAYMENT, {
          type: 'boleto',
          paymentId: data.payment_id,
          subscriptionId: data.subscription_id,
          boletoUrl: data.boleto_url,
          barcode: data.boleto_barcode,
          dueDate: data.due_date,
          value: data.value,
          cycle: data.cycle,
          isRecurring: data.is_recurring || false,
          addonId: addonId,
        });
        return { type: 'boleto', data };
      }

      return { type: 'success', data };
    } catch (error) {
      throwErrorMessage(error);
      return { type: 'error', error };
    } finally {
      commit(types.default.SET_BILLING_UI_FLAG, { isUpdating: false });
    }
  },

  // Remove addon
  removeAddon: async ({ commit, dispatch }, accountAddonId) => {
    commit(types.default.SET_BILLING_UI_FLAG, { isUpdating: true });
    try {
      await BillingAPI.removeAddon(accountAddonId);
      await dispatch('fetchBillingInfo');
      return true;
    } catch (error) {
      throwErrorMessage(error);
      return false;
    } finally {
      commit(types.default.SET_BILLING_UI_FLAG, { isUpdating: false });
    }
  },

  // Clear pending payment
  clearPendingPayment: ({ commit }) => {
    commit(types.default.SET_BILLING_PENDING_PAYMENT, null);
  },

  // Check payment status (for PIX/Boleto polling)
  checkPaymentStatus: async ({ commit, dispatch, state: $state }) => {
    try {
      // Get payment_id from pending payment (if available)
      const paymentId = $state.pendingPayment?.paymentId;
      const response = await BillingAPI.getPaymentStatus(paymentId);
      const { status, plan, addon, message } = response.data;

      if (status === 'confirmed') {
        // Payment confirmed - clear pending payment and refresh billing info
        commit(types.default.SET_BILLING_PENDING_PAYMENT, null);
        await dispatch('fetchBillingInfo');
        return { confirmed: true, plan, addon, message };
      }

      return { confirmed: false, status };
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('Failed to check payment status:', error);
      return { confirmed: false, error: true };
    }
  },

  // Fetch support contact info
  fetchSupportInfo: async ({ commit }) => {
    try {
      const response = await BillingAPI.getSupportInfo();
      commit(types.default.SET_BILLING_SUPPORT_INFO, response.data);
    } catch (error) {
      // Silently fail - support info is optional
      // eslint-disable-next-line no-console
      console.warn('Failed to fetch support info:', error);
    }
  },
};

export const mutations = {
  [types.default.SET_BILLING_UI_FLAG]($state, data) {
    $state.uiFlags = {
      ...$state.uiFlags,
      ...data,
    };
  },
  [types.default.SET_BILLING_SUBSCRIPTION]($state, subscription) {
    $state.subscription = subscription;
  },
  [types.default.SET_BILLING_ADDONS]($state, addons) {
    $state.addons = addons || [];
  },
  [types.default.SET_BILLING_LIMITS]($state, limits) {
    $state.limits = limits || {};
  },
  [types.default.SET_BILLING_USAGE]($state, usage) {
    $state.usage = usage || {};
  },
  [types.default.SET_BILLING_PLANS]($state, plans) {
    $state.plans = plans || [];
  },
  [types.default.SET_BILLING_AVAILABLE_ADDONS]($state, addons) {
    $state.availableAddons = addons || [];
  },
  [types.default.SET_BILLING_PAYMENT_METHODS]($state, methods) {
    $state.paymentMethods = methods || [];
  },
  [types.default.SET_BILLING_PENDING_PAYMENT]($state, payment) {
    $state.pendingPayment = payment;
  },
  [types.default.SET_BILLING_SUPPORT_INFO]($state, supportInfo) {
    $state.supportInfo = supportInfo || {
      email: '',
      whatsapp: '',
      inbox_id: '',
    };
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
